import {WinPresentationController} from "../../WinPresentationController";
import {TimelineLite} from "gsap";
import {BigWinView} from "./BigWinView";
import {WalkOfShame} from "../../../WalkOfShame";
import {WalkOfShameGameConfig} from "../../../WalkOfShameGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {WalkOfShameParsedGameData} from "../../../server/data/WalkOfShameParsedGameData";
import {IWinRatioConfig} from "../../../../core/config/WinRatio";
import {WalkOfShameSound} from "../../../sound/WalkOfShameSound";
import {Layers} from "../../../view/WalkOfShameLayers";

export class BigWinController extends WinPresentationController {
    private _view: BigWinView;
    private _levelTimeline: TimelineLite | null;
    private _soundName: string = "";

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    private static isLevelWinTooSmall(level: number, from: number, to: number): boolean {
        if (level === 0) {
            return false;
        }

        return (to - from) <= (from * 0.1);
    }

    protected hasWin(): boolean {
        return WalkOfShame.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation(): Promise<any> {
        await new Promise<any>(resolve => {
            this.playBigWinLevel(0, resolve);
        });
    }

    protected skip(): void {
        if (this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
        }
    }

    private playBigWinLevel(level: number, resolve: (value:any) => void): void {
        const data: WalkOfShameParsedGameData = WalkOfShame.model.parsedGameData;

        this._flowStateController.setSkipCallback();

        if (level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this.playEnding(resolve);
        } else {
            const isEndLevel: boolean = (level === data.currentWinRatioConfig!.bigWinLevel),
                currentRatio: IWinRatioConfig = WalkOfShameGameConfig.WIN_RATIOS.find((r: IWinRatioConfig) => (r.bigWinLevel === level))!,
                from: number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio,
                to: number = isEndLevel ? data.singleWin : data.playedBetValue * WalkOfShameGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === (level + 1))!.ratio,
                tooSmallEndLevel: boolean = isEndLevel && BigWinController.isLevelWinTooSmall(level, from, to),
                tl: TimelineLite = new TimelineLite();

            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, tooSmallEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            if (tooSmallEndLevel) {
                this._levelTimeline.progress(1);
            }
        }
    }

    private getLevelAnimation(level: number, duration: number, from: number, to: number, tooSmallEndLevel: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (level === 0) {
            const allWinPos: boolean[][] = WalkOfShame.model.parsedGameData.reelWinPositionsAll;

            tl.add(() => {
                WalkOfShameSound.soundPlayer.playTrack({
                    name: WalkOfShameSound.BIG_WIN_INTRO
                });
            });
            allWinPos.forEach((winPos: boolean[], reelId: number) => {
                winPos.forEach((win: boolean, symId: number) => {
                    if (win) {
                        tl.add(WalkOfShame.reels[reelId].getSymbol((symId + 1)).getShakeAnimation(0.97), 0);
                    }
                });
            });
        }
        tl.add([
            () => {
                if (level === 0) {
                    WalkOfShameSound.fadeMusic(0, 300);

                    WalkOfShame.logo.pauseParachutesOnBigWin();
                }

                if (level > 0) {
                    WalkOfShameSound.soundPlayer.stopTrack(WalkOfShameSound.BIG_WIN[level - 1]);
                }

                this._soundName = WalkOfShameSound.BIG_WIN[level];
                WalkOfShameSound.soundPlayer.playTrack({
                    name: this._soundName
                });

                if (level === 1) {
                    WalkOfShame.particlesManager.playConfetti();
                } else if (level === 2) {
                    WalkOfShame.particlesManager.playBottomChampagne();
                } else if (level === 3) {
                    WalkOfShame.particlesManager.boostBottomChampagne();
                } else if (level === 4) {
                    WalkOfShame.particlesManager.playShower();
                }

                if (!tooSmallEndLevel) {
                    this._flowStateController.setSkipCallback(() => this.skip!());
                }
            },
            WalkOfShame.bigWinCountUp.getCountUpAnimation(from, to, duration),
            this._view.getLevelAnimation(level)
        ]);

        return tl
    }

    private playEnding(resolve: (value:any) => void): void {
        if (this._soundName) {
            WalkOfShameSound.soundPlayer.stopTrack(this._soundName);
        }
        WalkOfShameSound.soundPlayer.playTrack({
            name: WalkOfShameSound.BIG_WIN_END
        });
        this._soundName = "";

        // #107
        if (WalkOfShame.model.parsedGameData.mode.indexOf("RESPIN") > -1 && WalkOfShame.model.parsedGameData.nextMode.indexOf("RESPIN") === -1) {
            WalkOfShameSound.currentMusic = WalkOfShameSound.getMusicNameByMode(WalkOfShame.model.parsedGameData.nextMode);
        }

        WalkOfShame.winWays.showWinWays(WalkOfShame.model.parsedGameData.totalWaysWon, 580);
        WalkOfShame.bigWinCountUp.stay().eventCallback("onComplete", () => {
            if (WalkOfShame.model.parsedGameData.brokeTheBank) {
                WalkOfShame.blackOverlay.showOverlay(0.01, 1, Layers.BLACK_OVERLAY_TOP);
            }

            this._view.playEnding();
            WalkOfShame.bigWinCountUp.remove();

            WalkOfShame.logo.resumeParachutesOnBigWin();

            resolve(undefined);
        });
        if (!WalkOfShame.model.parsedGameData.brokeTheBank) {
            WalkOfShameSound.fadeMusic(1, 1000);

            WalkOfShame.keypadPlugin.setWin(WalkOfShame.model.parsedGameData.totalWin, WalkOfShame.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        }
        WalkOfShame.particlesManager.stopAll();
    }
}